import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Copy, Download, Loader2, Plug, ShieldAlert, XCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

const INSTALL_GUIDE = `# Instalare go2rtc gateway pe serverul dvs.

1. Descărcați binarul de pe https://github.com/AlexxIT/go2rtc/releases (Linux, Windows sau Raspberry Pi).
2. Apăsați butonul "Descarcă go2rtc.yaml" de pe această pagină și plasați-l lângă binar.
3. Porniți gateway-ul cu:
   ./go2rtc -config go2rtc.yaml
4. Asigurați accesul HTTPS la portul 1984 (un Caddy/Traefik reverse-proxy este suficient).
5. Completați mai jos URL-ul public al gateway-ului (ex: https://gateway.exemplu.ro) și salvați.
6. În pagina "Camere" completați IP, port RTSP și credențiale Reolink pentru fiecare cameră.
7. În pagina "Live View" veți primi automat un flux HLS prin gateway.`;

export default function SettingsPage() {
  const { isAdmin } = useAuth();
  const qc = useQueryClient();
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: api.settings.get, enabled: isAdmin });
  const { data: config } = useQuery({ queryKey: ["gateway-config"], queryFn: api.gateway.config, enabled: isAdmin });
  const updateMut = useMutation({ mutationFn: api.settings.update, onSuccess: () => { toast.success("Setări salvate"); qc.invalidateQueries({ queryKey: ["settings"] }); qc.invalidateQueries({ queryKey: ["gateway-config"] }); }, onError: (e) => toast.error(apiError(e)) });
  const [gwResult, setGwResult] = useState(null);
  const testGw = useMutation({ mutationFn: api.gateway.test, onSuccess: (r) => { setGwResult(r); r.reachable ? toast.success("Gateway accesibil") : toast.error("Gateway inaccesibil"); }, onError: (e) => toast.error(apiError(e)) });

  const [gatewayUrl, setGatewayUrl] = useState("");
  const [aiEnabled, setAiEnabled] = useState(true);
  const [aiOnlyMotion, setAiOnlyMotion] = useState(true);
  useEffect(() => {
    if (settings) { setGatewayUrl(settings.gatewayBaseUrl || ""); setAiEnabled(!!settings.aiEnabled); setAiOnlyMotion(settings.aiOnlyMotion !== false); }
  }, [settings]);

  if (!isAdmin) {
    return <DashboardLayout><div className="max-w-2xl mx-auto mt-12"><Card><CardContent className="flex flex-col items-center gap-3 py-12 text-center"><ShieldAlert className="h-10 w-10 text-muted-foreground" /><p className="text-lg font-medium">Acces restricționat</p><p className="text-sm text-muted-foreground">Doar administratorii pot accesa setările aplicației.</p></CardContent></Card></div></DashboardLayout>;
  }

  const downloadConfig = () => {
    if (!config?.config) return;
    const blob = new Blob([config.config], { type: "text/yaml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "go2rtc.yaml"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="settings-page">
        <div>
          <h1 className="text-2xl font-semibold">Setări & Gateway</h1>
          <p className="text-sm text-muted-foreground">Configurare gateway go2rtc pentru live streaming RTSP → HLS și activare detecție AI.</p>
        </div>

        <Card>
          <CardHeader><CardTitle>Gateway go2rtc</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="gateway">URL public gateway (ex: https://gateway.exemplu.ro)</Label>
              <Input id="gateway" value={gatewayUrl} placeholder="https://..." onChange={(e) => setGatewayUrl(e.target.value)} data-testid="gateway-url-input" />
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div><p className="text-sm font-medium">Detecție AI evenimente</p><p className="text-xs text-muted-foreground">Rulează automat pe fiecare clip nou (Vehicle / Person / Animal / Others).</p></div>
              <Switch checked={aiEnabled} onCheckedChange={setAiEnabled} data-testid="ai-enabled-switch" />
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div><p className="text-sm font-medium">Procesează AI doar pe clipurile cu mișcare</p><p className="text-xs text-muted-foreground">Clipurile marcate explicit „fără mișcare" (programate, timelapse, RH, S) sunt sărite. Recomandat pentru economie LLM.</p></div>
              <Switch checked={aiOnlyMotion} onCheckedChange={setAiOnlyMotion} />
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button onClick={() => updateMut.mutate({ gatewayBaseUrl: gatewayUrl, aiEnabled, aiOnlyMotion })} disabled={updateMut.isPending} data-testid="save-settings-btn">Salvează</Button>
              <Button variant="outline" onClick={() => testGw.mutate()} disabled={testGw.isPending} data-testid="test-gateway-btn">{testGw.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plug className="h-4 w-4 mr-2" />}Testează gateway</Button>
              <Button variant="outline" onClick={downloadConfig} disabled={!config?.config}><Download className="h-4 w-4 mr-2" />Descarcă go2rtc.yaml</Button>
              <Button variant="outline" onClick={() => { if (config?.config) { navigator.clipboard.writeText(config.config); toast.success("Config copiat"); } }}><Copy className="h-4 w-4 mr-2" />Copiază config</Button>
            </div>
            {gwResult && (
              <div className={`rounded-md border p-3 text-sm ${gwResult.reachable ? "bg-green-50 border-green-200 text-green-700" : "bg-red-50 border-red-200 text-red-700"}`}>
                <p className="flex items-center gap-2">{gwResult.reachable ? <CheckCircle2 className="h-4 w-4 shrink-0" /> : <XCircle className="h-4 w-4 shrink-0" />}{gwResult.message}</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Ghid instalare gateway</CardTitle></CardHeader>
          <CardContent><pre className="whitespace-pre-wrap text-xs bg-muted p-4 rounded-lg leading-relaxed">{INSTALL_GUIDE}</pre></CardContent>
        </Card>

        {config?.config && (
          <Card>
            <CardHeader><CardTitle>Preview go2rtc.yaml</CardTitle></CardHeader>
            <CardContent><pre className="whitespace-pre-wrap text-xs bg-muted p-4 rounded-lg overflow-auto">{config.config}</pre></CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
